// What the user changed in the shipment details the AI wizard filled in. Used by the server, which
// records the change as a correction when the opportunity is saved, and by the Shipment Details
// panel, which then asks (optionally) why the AI got it wrong. The same groups as the training set
// (docs/inquiry-agent/README.md): mode, incoterm, routes, origin / destination, cargo.

const CARGO_FIELDS = [
    ['quantity', 'quantity'], ['weight', 'weight'], ['volume', 'volume'],
    ['length', 'dimensions'], ['width', 'dimensions'], ['height', 'dimensions'],
    ['dangerous', 'dangerous goods'], ['temperature', 'temperature'],
];

// Product names the form rewrites on its own are not a correction.
function productType(type) {
    const t = String(type || '').trim().toLowerCase();
    if (t.includes('(sea)') || t === 'lcl') return 'lcl';
    if (t.includes('(air)') || t === 'air freight') return 'air freight';
    return t;
}

// LCL and air cargo is one product with a line per package (`calculations`); compare line by line.
export function cargoLines(products) {
    return (products || []).flatMap(p => (Array.isArray(p?.calculations) && p.calculations.length
        ? p.calculations.map((c, i) => ({ ...p, ...c, calculations: undefined, id: i === 0 ? p.id : c.id, type: p.type }))
        : [p]));
}

function placeId(p) {
    if (!p) return '';
    return String(p.id || p.text || '').toUpperCase();
}

function placeText(p) {
    return p ? (p.text || p.id || '') : '';
}

const num = v => (v === null || v === undefined || v === '' || Number.isNaN(Number(v)) ? null : Math.round(Number(v) * 100) / 100);

function sameValue(field, a, b) {
    if (field === 'dangerous') return !!a === !!b;
    if (field === 'temperature') return String(a ?? '') === String(b ?? '');
    return num(a) === num(b) || (!num(a) && !num(b));
}

function lineSummary(p) {
    if (!p) return null;
    const parts = [p.type, p.quantity ? `× ${p.quantity}` : null, p.weight ? `${p.weight} ${p.kgOrLbs || 'kg'}` : null,
        p.length && p.width && p.height ? `${p.length}×${p.width}×${p.height} ${p.cmOrInches || 'cm'}` : null, p.volume ? `${p.volume} cbm` : null];
    return parts.filter(Boolean).join(', ');
}

/**
 * filled: what the AI put in the form { transportationMethod, incoterm, routes, products }
 * saved:  the shipment details now  { transportationMethod, incoterm, routes, products }
 * Returns [{ kind, what, ai, saved }] in plain words; empty when nothing was changed.
 */
export function aiParseDifferences(filled, saved) {
    if (!filled || !saved) return [];
    const out = [];
    if ((filled.transportationMethod || '') !== (saved.transportationMethod || '')) {
        out.push({ kind: 'mode', what: 'mode', ai: filled.transportationMethod || null, saved: saved.transportationMethod || null });
    }
    if ((filled.incoterm || '') !== (saved.incoterm || '')) {
        out.push({ kind: 'incoterm', what: 'incoterm', ai: filled.incoterm || null, saved: saved.incoterm || null });
    }
    const fr = filled.routes || [];
    const sr = saved.routes || [];
    if (fr.length !== sr.length) out.push({ kind: 'routes', what: 'number of routes', ai: fr.length, saved: sr.length });
    fr.slice(0, sr.length).forEach((r, i) => {
        [['source', 'origin'], ['destination', 'destination']].forEach(([side, name]) => {
            if (placeId(r?.[side]) !== placeId(sr[i]?.[side])) {
                out.push({ kind: name, what: fr.length > 1 ? `${name} (route ${i + 1})` : name, ai: placeText(r?.[side]) || null, saved: placeText(sr[i]?.[side]) || null });
            }
        });
    });

    const fl = cargoLines(filled.products);
    const sl = cargoLines(saved.products);
    const byId = new Map(sl.filter(p => p?.id).map(p => [p.id, p]));
    const pairs = [];
    const restF = [];
    fl.forEach(p => {
        const match = p?.id && byId.get(p.id);
        if (match) { pairs.push([p, match]); byId.delete(p.id); } else restF.push(p);
    });
    const restS = sl.filter(p => !p?.id || byId.has(p.id));
    restF.forEach((p, i) => (restS[i] ? pairs.push([p, restS[i]]) : out.push({ kind: 'cargo', what: 'cargo line removed', ai: lineSummary(p), saved: null })));
    restS.slice(restF.length).forEach(p => out.push({ kind: 'cargo', what: 'cargo line added', ai: null, saved: lineSummary(p) }));
    pairs.forEach(([a, b]) => {
        if (productType(a.type) !== productType(b.type)) out.push({ kind: 'cargo', what: 'cargo type', ai: a.type || null, saved: b.type || null });
        const seen = new Set();
        CARGO_FIELDS.forEach(([field, label]) => {
            if (seen.has(label) || sameValue(field, a[field], b[field])) return;
            seen.add(label);
            const dims = p => [p.length, p.width, p.height].map(x => x ?? '—').join('×');
            out.push({ kind: 'cargo', what: `cargo ${label}`, ai: label === 'dimensions' ? dims(a) : (a[field] ?? null), saved: label === 'dimensions' ? dims(b) : (b[field] ?? null) });
        });
    });
    return out;
}

// The wizard's answer kept on the opportunity: the most recent update that carries one.
export function latestAiParse(updates) {
    const list = (updates || []).filter(u => u?.aiParse && !u.deleted);
    return list.length ? list.reduce((a, b) => ((b.created || 0) > (a.created || 0) ? b : a)).aiParse : null;
}
